import React from 'react';
import axios from 'axios';
import { useEffect, useState } from 'react';
import OAEGraph from './OAEGraph';
import {
  freqsFor, pointAt, overallResult, bilateralSummary, normaliseOAE, fmtFreq,
} from './oaeLogic';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * OAEReportView — read-only OAE report canvas.
 *
 * Used both inside the OAEReportModal (audiologist preview + print)
 * and by the OAEReportSection (embedded in the standard hearing report
 * when the OAE section is enabled).
 *
 * Layout (A4 portrait):
 *   • Clinic letterhead
 *   • Title + subtitle (test type + purpose)
 *   • Patient + test parameters header grid
 *   • Two-column ear layout (graph + table + Pass/Refer badge)
 *   • Bilateral summary
 *   • Test Comments + Interpretation side by side
 *   • Recommendations
 *   • Clinic footer + signature block
 */
const OAEReportView = ({ oae, patient, clinic, audiologistName, license, sessionId }) => {
  const state = normaliseOAE(oae);
  const freqs = freqsFor(state.test_type);
  const rightResult = overallResult(state.right, freqs);
  const leftResult  = overallResult(state.left, freqs);
  const bilateral   = bilateralSummary(rightResult, leftResult);

  const printedOn = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  const EarBlock = ({ ear, result }) => {
    const label = ear === 'right' ? 'Right Ear' : 'Left Ear';
    const badgeCls = result === 'Pass'
      ? 'bg-emerald-600 text-white'
      : result === 'Refer'
        ? 'bg-red-600 text-white'
        : 'bg-slate-400 text-white';
    return (
      <div className="flex flex-col border border-slate-400 rounded overflow-hidden">
        <div className="flex items-center justify-between px-2 py-1 bg-slate-100 border-b border-slate-300">
          <span className={`text-xs font-bold ${ear === 'right' ? 'text-red-700' : 'text-blue-700'}`}>{label}</span>
          <span
            data-testid={`oae-report-badge-${ear}`}
            className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${badgeCls}`}
          >
            {result || 'NO DATA'}
          </span>
        </div>
        <div className="p-1.5">
          <OAEGraph
            ear={ear}
            earData={state[ear]}
            freqs={freqs}
            plotKind={null}
            readOnly
            passResult={result}
            width={460}
            height={220}
          />
        </div>
        <table className="w-full text-[10px] border-t border-slate-300" data-testid={`oae-report-table-${ear}`}>
          <thead className="bg-slate-50">
            <tr>
              <th className="border border-slate-300 px-1 py-0.5">Freq</th>
              <th className="border border-slate-300 px-1 py-0.5">OAE</th>
              <th className="border border-slate-300 px-1 py-0.5">Noise</th>
              <th className="border border-slate-300 px-1 py-0.5">SNR</th>
              <th className="border border-slate-300 px-1 py-0.5">Result</th>
            </tr>
          </thead>
          <tbody>
            {freqs.map((f) => {
              const { oae: o, noise: n, snr, result: r } = pointAt(state[ear], f);
              return (
                <tr key={f}>
                  <td className="border border-slate-300 px-1 py-0.5 text-center font-mono">{fmtFreq(f)}k</td>
                  <td className="border border-slate-300 px-1 py-0.5 text-center font-mono">{o ?? '—'}</td>
                  <td className="border border-slate-300 px-1 py-0.5 text-center font-mono">{n ?? '—'}</td>
                  <td className="border border-slate-300 px-1 py-0.5 text-center font-mono">{snr === null ? '—' : snr.toFixed(1)}</td>
                  <td
                    className={`border border-slate-300 px-1 py-0.5 text-center font-bold ${
                      r === 'Pass' ? 'text-emerald-700' : r === 'Refer' ? 'text-red-700' : 'text-slate-400'
                    }`}
                  >{r || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div
      id="oae-report-canvas"
      data-testid="oae-report-canvas"
      className="oae-report bg-white text-slate-900"
      style={{ width: '210mm', minHeight: '297mm', padding: '10mm', boxSizing: 'border-box', fontFamily: 'Arial, sans-serif' }}
    >
      {/* ── Clinic letterhead ───────────────────────────────────── */}
      <div className="flex items-center border-b-2 border-slate-800 pb-2 mb-2" data-testid="oae-report-letterhead">
        {clinic?.logo_base64 && (
          <img src={clinic.logo_base64} alt="logo" style={{ width: 60, height: 60, objectFit: 'contain', marginRight: 10 }} />
        )}
        <div className="flex-1">
          <div className="text-xl font-bold text-slate-900">{clinic?.name || 'Audiology Clinic'}</div>
          {clinic?.tagline && <div className="text-xs italic text-slate-600">{clinic.tagline}</div>}
          <div className="text-[10px] text-slate-700 mt-0.5">
            {clinic?.address_line1}{clinic?.address_line2 ? ` · ${clinic.address_line2}` : ''}
          </div>
          <div className="text-[10px] text-slate-700">
            {clinic?.tel ? `Tel: ${clinic.tel}` : ''}{clinic?.email ? ` · Email: ${clinic.email}` : ''}
          </div>
        </div>
      </div>

      {/* ── Title + subtitle ────────────────────────────────────── */}
      <div className="text-center mb-2">
        <h1 className="text-lg font-black uppercase tracking-wider text-slate-900" data-testid="oae-report-title">
          Otoacoustic Emission (OAE) Report
        </h1>
        <div className="text-[11px] text-slate-700 mt-0.5" data-testid="oae-report-subtitle">
          <span className="font-bold">{state.test_type}</span> · {state.purpose}
        </div>
      </div>

      {/* ── Patient + test parameters ───────────────────────────── */}
      <div className="grid grid-cols-4 gap-x-3 gap-y-1 text-[10px] border border-slate-300 rounded p-2 mb-2 bg-slate-50" data-testid="oae-report-header-grid">
        <div><span className="font-bold text-slate-600">Patient:</span> {patient?.name || '—'}</div>
        <div><span className="font-bold text-slate-600">MRD:</span> {patient?.mrd || patient?.patient_id || '—'}</div>
        <div><span className="font-bold text-slate-600">Age / Sex:</span> {patient?.age ?? '—'}{patient?.gender ? ` / ${patient.gender[0]?.toUpperCase()}` : ''}</div>
        <div><span className="font-bold text-slate-600">Date:</span> {printedOn}</div>
        <div><span className="font-bold text-slate-600">Probe:</span> {state.probe_type || '—'}</div>
        <div><span className="font-bold text-slate-600">Stimulus:</span> {state.stimulus || '—'}</div>
        <div><span className="font-bold text-slate-600">Equipment:</span> {state.equipment || '—'}</div>
        <div><span className="font-bold text-slate-600">Environment:</span> {state.environment}</div>
      </div>

      {/* ── Two-column ear layout ───────────────────────────────── */}
      <div className="grid grid-cols-2 gap-2 mb-2" data-testid="oae-report-two-column">
        <EarBlock ear="right" result={rightResult} />
        <EarBlock ear="left"  result={leftResult} />
      </div>

      {/* ── Bilateral summary ───────────────────────────────────── */}
      {bilateral && (
        <div
          data-testid="oae-report-bilateral"
          className={`text-center py-1 mb-2 rounded font-bold text-sm ${
            bilateral === 'Bilateral Pass'
              ? 'bg-emerald-100 text-emerald-800 border border-emerald-400'
              : bilateral === 'Bilateral Refer'
                ? 'bg-red-100 text-red-800 border border-red-400'
                : 'bg-amber-100 text-amber-800 border border-amber-400'
          }`}
        >
          <span className="mr-2">{bilateral === 'Bilateral Pass' ? '✓' : bilateral === 'Bilateral Refer' ? '✗' : '⚠'}</span>
          Summary: {bilateral}
        </div>
      )}

      {/* ── Test Comments + Interpretation side by side ─────────── */}
      <div className="grid grid-cols-2 gap-2 mb-2">
        <div className="border border-slate-300 rounded p-1.5">
          <div className="text-[10px] font-bold uppercase text-slate-600 mb-0.5">Test Comments</div>
          <div className="text-[10px] whitespace-pre-wrap text-slate-800 min-h-[36px]" data-testid="oae-report-comments">
            {state.comments || '—'}
          </div>
        </div>
        <div className="border border-slate-300 rounded p-1.5">
          <div className="text-[10px] font-bold uppercase text-slate-600 mb-0.5">Interpretation</div>
          <div className="text-[10px] whitespace-pre-wrap text-slate-800 min-h-[36px]" data-testid="oae-report-interpretation">
            {state.interpretation}
          </div>
        </div>
      </div>

      {/* ── Recommendations ─────────────────────────────────────── */}
      <div className="border border-slate-300 rounded p-1.5 mb-2">
        <div className="text-[10px] font-bold uppercase text-slate-600 mb-0.5">Recommendations</div>
        <div className="text-[10px] whitespace-pre-wrap text-slate-800 min-h-[24px]" data-testid="oae-report-recommendations">
          {state.recommendations || '—'}
        </div>
      </div>

      {/* ── Footer + signature ──────────────────────────────────── */}
      <div className="flex items-end justify-between mt-3 pt-2 border-t border-slate-300 text-[10px]" data-testid="oae-report-footer">
        <div className="text-slate-600">
          <div className="italic">
            {clinic?.address_line1}{clinic?.address_line2 ? `, ${clinic.address_line2}` : ''}
          </div>
          {(clinic?.tel || clinic?.email) && (
            <div className="italic">
              {clinic?.tel}{clinic?.tel && clinic?.email ? ' · ' : ''}{clinic?.email}
            </div>
          )}
          {sessionId && <div className="mt-1 text-[9px] text-slate-500">Ref: {sessionId}</div>}
        </div>
        <div className="text-right">
          <div className="border-t border-slate-500 pt-0.5 min-w-[140px]">
            <div className="font-bold text-slate-800">{audiologistName || 'Audiologist'}</div>
            {license && <div className="text-slate-600">License No.: {license}</div>}
            <div className="text-[9px] text-slate-500">Audiologist Signature</div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Convenience wrapper that fetches clinic + signature license from
 * /api/settings/clinic + the current session's audiologist_user_id.
 * Used by OAEReportModal to avoid duplicating fetch logic.
 */
export const OAEReportViewWithFetch = ({ oae, patient, sessionId, audiologistName }) => {
  const [clinic, setClinic] = useState({ name: '', tagline: '', address_line1: '', address_line2: '', tel: '', email: '', logo_base64: null });
  const [license, setLicense] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/settings/clinic${sessionId ? `?session_id=${sessionId}` : ''}`);
        const s = r.data || {};
        if (!alive) return;
        setClinic({
          name: s.name || '',
          tagline: s.tagline || '',
          address_line1: s.address || '',
          address_line2: [s.city, s.state, s.pincode].filter(Boolean).join(', '),
          tel: s.phone || '',
          email: s.email || '',
          logo_base64: null,
        });
        try {
          const lr = await axios.get(`${API}/settings/clinic/logo`, { responseType: 'blob' });
          if (!alive) return;
          const url = URL.createObjectURL(lr.data);
          setClinic((prev) => ({ ...prev, logo_base64: url }));
        } catch { /* no logo — ignore */ }
      } catch { /* fallback ok */ }
      // license via session audiologist user id
      try {
        const sr = await axios.get(`${API}/sessions/${sessionId}`);
        const uid = sr.data?.audiologist_user_id;
        if (!uid || !alive) return;
        const lr = await axios.get(`${API}/settings/users/${uid}/signature`, { responseType: 'blob' });
        const lic = lr?.headers?.['x-license-no'] || lr?.headers?.['X-License-No'] || '';
        if (alive) setLicense(lic);
      } catch { /* no signature/license — ok */ }
    })();
    return () => { alive = false; };
  }, [sessionId]);

  return (
    <OAEReportView
      oae={oae}
      patient={patient}
      clinic={clinic}
      audiologistName={audiologistName}
      license={license}
      sessionId={sessionId}
    />
  );
};

export default OAEReportView;
